import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Settings = () => {
  const { user, updateUser, isAdmin } = useAuth();
  const { t, i18n } = useTranslation();

  // Determine available tabs based on role
  const isTeamOwner = user?.role === 'team_owner' || user?.role === 'admin';
  const showTeamTab = isTeamOwner && user?.teamId;
  const showLeagueTab = typeof isAdmin === 'function' ? isAdmin() : user?.role === 'admin';

  const [activeTab, setActiveTab] = useState('profile');

  // --- Profile tab state ---
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    firstName: '',
    gamertag: '',
    platform: '',
    twitchProfile: '',
    preferredLanguage: '',
    emailDigestOptIn: true
  });

  // --- Team Settings tab state ---
  const [teamLoading, setTeamLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [cantCutPlayer, setCantCutPlayer] = useState(null);
  const [cantCutRuleActive, setCantCutRuleActive] = useState(true);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamSuccess, setTeamSuccess] = useState('');
  const [activeSeason, setActiveSeason] = useState(null);

  // --- League Settings tab state ---
  const [weights, setWeights] = useState(null);
  const [weightsSaving, setWeightsSaving] = useState(false);
  const [weightsMessage, setWeightsMessage] = useState({ type: '', text: '' });

  // ========================
  // Profile tab logic
  // ========================
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        gamertag: user.gamertag || '',
        platform: user.platform || '',
        twitchProfile: user.twitchProfile || '',
        preferredLanguage: user.preferredLanguage || 'de',
        emailDigestOptIn: user.emailDigestOptIn !== false
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const cleanedFormData = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (typeof value === 'boolean' || (value && value.toString().trim() !== '') || key === 'preferredLanguage') {
          cleanedFormData[key] = value === '' && key === 'preferredLanguage' ? 'de' : value;
        }
      });

      const response = await axios.put(
        `${API_URL}/auth/update-profile`,
        cleanedFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (updateUser) {
        updateUser(response.data.user);
      }

      if (formData.preferredLanguage && formData.preferredLanguage !== i18n.language) {
        i18n.changeLanguage(formData.preferredLanguage);
        localStorage.setItem('selectedLanguage', formData.preferredLanguage);
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to update profile'
      });
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    if (platform === 'Xbox') return '🎮';
    if (platform === 'PlayStation') return '🎯';
    return '🎮';
  };

  // ========================
  // Team Settings tab logic
  // ========================
  const fetchActiveSeason = useCallback(async () => {
    try {
      const response = await fetch('/api/seasons/active');
      const data = await response.json();
      if (data.success) {
        setActiveSeason(data.season);
      } else {
        setTeamError('No active season found');
        setTeamLoading(false);
      }
    } catch (error) {
      console.error('Error fetching active season:', error);
      setTeamError(t('admin.errorFetchingSeason'));
      setTeamLoading(false);
    }
  }, [t]);

  const fetchTeamPlayers = useCallback(async () => {
    try {
      setTeamLoading(true);
      const response = await fetch(`/api/cantcut/team/${user.teamId}/season/${activeSeason.id}`);
      const data = await response.json();

      if (data.error) {
        if (data.migrationNeeded) {
          setTeamError('Database migration required. Please contact administrator to enable Can\'t Cut functionality.');
        } else {
          setTeamError(data.error || t('admin.errorLoadingPlayers'));
        }
      } else {
        setTeamPlayers(data.players || []);
        setCantCutPlayer(data.currentCantCutPlayer);
        setCantCutRuleActive(data.cantCutRuleActive !== false);
        if (data.currentCantCutPlayer) {
          setSelectedPlayerId(data.currentCantCutPlayer.id);
        }
        if (data.migrationNeeded) {
          setTeamError('Can\'t Cut functionality is not fully enabled yet. Database migration required.');
        }
      }
    } catch (error) {
      console.error('Error fetching team players:', error);
      setTeamError(t('admin.errorLoadingPlayers'));
    } finally {
      setTeamLoading(false);
    }
  }, [user?.teamId, activeSeason?.id, t]);

  useEffect(() => {
    if (showTeamTab) {
      fetchActiveSeason();
    }
  }, [showTeamTab, fetchActiveSeason]);

  useEffect(() => {
    if (activeSeason?.id && user?.teamId) {
      fetchTeamPlayers();
    } else if (activeSeason?.id && !user?.teamId) {
      setTeamLoading(false);
    }
  }, [activeSeason?.id, user?.teamId, fetchTeamPlayers]);

  const handleCantCutSubmit = async () => {
    if (!selectedPlayerId) {
      setTeamError(t('admin.pleaseSelectPlayer'));
      return;
    }
    if (cantCutPlayer) {
      setTeamError(t('admin.cantCutAlreadySet'));
      return;
    }
    setShowConfirmation(true);
  };

  const confirmCantCutSelection = async () => {
    try {
      setSaving(true);
      setTeamError('');
      setTeamSuccess('');
      setShowConfirmation(false);

      const response = await fetch('/api/cantcut/set', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          teamId: user.teamId,
          seasonId: activeSeason.id,
          playerId: selectedPlayerId
        }),
      });

      if (!response.ok) {
        if (response.status === 502) {
          throw new Error('Server error: Can\'t Cut API route not available. Please contact administrator.');
        } else if (response.status === 404) {
          throw new Error('API endpoint not found. Please contact administrator.');
        } else {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned invalid response. Please contact administrator.');
      }

      const data = await response.json();

      if (data.success) {
        setCantCutPlayer(data.player);
        setTeamSuccess(t('admin.cantCutPlayerUpdated'));
        fetchTeamPlayers();
        setTimeout(() => setTeamSuccess(''), 3000);
      } else {
        setTeamError(data.error || t('admin.errorUpdatingCantCut'));
      }
    } catch (error) {
      console.error('Error updating cant cut player:', error);
      setTeamError(error.message || t('admin.errorUpdatingCantCut'));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  // ========================
  // Tab definitions
  // ========================
  const tabs = [
    { id: 'profile', label: '👤 Profile', show: true },
    { id: 'team', label: '🏀 Team Settings', show: showTeamTab },
    { id: 'league', label: '⚙️ League Settings', show: showLeagueTab }
  ].filter(tab => tab.show);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Settings</h1>

        {/* Tab Navigation */}
        {tabs.length > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-1 inline-flex mb-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-md text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:bg-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* ======================== */}
        {/* Profile Tab */}
        {/* ======================== */}
        {activeTab === 'profile' && (
          <>
            {/* User Info Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-6 mb-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-3xl">
                  {user.firstName?.charAt(0) || user.username?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{user.firstName || user.username}</h2>
                  <p className="text-gray-600 dark:text-gray-400">@{user.username}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded text-xs font-semibold">
                      {user.role === 'admin' ? '👑 Admin' : user.role === 'team_owner' ? '🏀 Team Owner' : '⏳ Pending'}
                    </span>
                    {user.team && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 rounded text-xs font-semibold">
                        📍 {user.team.abbreviation}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {message.text && (
                <div className={`mb-4 p-4 rounded ${
                  message.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700 dark:text-green-400'
                    : 'bg-red-50 border border-red-200 text-red-700 dark:text-red-400'
                }`}>
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
                  <input id="firstName" name="firstName" type="text" value={formData.firstName} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Your first name" />
                </div>

                <div>
                  <label htmlFor="gamertag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">NBA 2K Gamertag</label>
                  <input id="gamertag" name="gamertag" type="text" value={formData.gamertag} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Your NBA 2K username" />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This is the name you use in NBA 2K</p>
                </div>

                <div>
                  <label htmlFor="platform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gaming Platform</label>
                  <select id="platform" name="platform" value={formData.platform} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:bg-gray-700 dark:text-white dark:border-gray-600">
                    <option value="">Select your platform</option>
                    <option value="Xbox">🎮 Xbox Series S/X</option>
                    <option value="PlayStation">🎯 PlayStation 5</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Select the console you're playing NBA 2K on.</p>
                </div>

                <div>
                  <label htmlFor="twitchProfile" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">🎮 Twitch Profile (Optional)</label>
                  <input id="twitchProfile" name="twitchProfile" type="text" value={formData.twitchProfile} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    placeholder="Your Twitch username" />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter your Twitch username to be featured in the Team Owners directory. Just the username, not the full URL.
                  </p>
                </div>

                <div>
                  <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">🌐 Preferred Language</label>
                  <select id="preferredLanguage" name="preferredLanguage" value={formData.preferredLanguage} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:bg-gray-700 dark:text-white dark:border-gray-600">
                    <option value="de">🇩🇪 Deutsch (German)</option>
                    <option value="en">🇺🇸 English</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Your preferred language for the interface.</p>
                </div>

                {(user.role === 'team_owner' || user.role === 'admin') && (
                  <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
                    <input id="emailDigestOptIn" name="emailDigestOptIn" type="checkbox"
                      checked={formData.emailDigestOptIn}
                      onChange={(e) => { setFormData({ ...formData, emailDigestOptIn: e.target.checked }); setMessage({ type: '', text: '' }); }}
                      className="mt-1 h-4 w-4 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600" />
                    <div>
                      <label htmlFor="emailDigestOptIn" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        📧 Täglicher Digest per E-Mail
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Erhalte jeden Morgen eine E-Mail mit den gestrigen Ergebnissen, deinem Saison-Fortschritt und aktuellen Trade-Anfragen.
                      </p>
                    </div>
                  </div>
                )}

                {user.platform && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{getPlatformIcon(user.platform)}</span>
                      <div>
                        <p className="font-semibold text-gray-800 dark:text-gray-200">Current Platform: {user.platform}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{user.platform === 'Xbox' ? 'Xbox Series S/X' : 'PlayStation 5'}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Account Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Username:</span>
                      <span className="font-medium">{user.username}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Email:</span>
                      <span className="font-medium">{user.email}</span>
                    </div>
                    {user.team && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Team:</span>
                        <span className="font-medium">{user.team.city} {user.team.name}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Account Status:</span>
                      <span className="font-medium capitalize">{user.status}</span>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">To change your username or email, please contact an administrator.</p>
                </div>

                <div className="pt-4">
                  <button type="submit" disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">💡 Need Help?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                If you need to change your username, email, or have any other account issues, please contact an administrator.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Switching platforms?</strong> No problem! Just update your platform selection above.
              </p>
            </div>
          </>
        )}

        {/* ======================== */}
        {/* Team Settings Tab */}
        {/* ======================== */}
        {activeTab === 'team' && showTeamTab && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">{t('admin.teamSettings')}</h2>

            {/* Can't Cut Player Section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">{t('admin.cantCutPlayer')}</h3>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-800 mb-2">{t('admin.cantCutRules')}</h4>
                <ul className="text-blue-700 dark:text-blue-400 text-sm space-y-1">
                  <li>• {t('admin.cantCutRule1')}</li>
                  <li>• {t('admin.cantCutRule2')}</li>
                  <li>• {t('admin.cantCutRule3')}</li>
                  <li>• {t('admin.cantCutRule4')}</li>
                </ul>
              </div>

              {teamError && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">{teamError}</div>
              )}
              {teamSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 dark:text-green-400 px-4 py-3 rounded mb-4">{teamSuccess}</div>
              )}

              {teamLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading team settings...</div>
              ) : (
                <>
                  {!cantCutRuleActive && (
                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                      <div className="flex items-center justify-center mb-4">
                        <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-2xl">🚫</span>
                        </div>
                      </div>
                      <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Can't Cut Rules Disabled</h4>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Can't Cut player rules are currently disabled for this season. All players can be traded freely.
                      </p>
                    </div>
                  )}

                  {cantCutRuleActive && (
                    <>
                      {cantCutPlayer && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                          <h4 className="font-medium text-green-800 mb-2">{t('admin.currentCantCutPlayer')}</h4>
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                              <span className="text-xl">🏀</span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 dark:text-gray-200">{cantCutPlayer.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.overall')}: {cantCutPlayer.overallRating}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {teamPlayers && teamPlayers.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-4">
                            {cantCutPlayer ? t('admin.selectNewCantCutPlayer') : t('admin.selectCantCutPlayer')}
                          </h4>

                          {cantCutPlayer && (
                            <div className="bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-4">
                              <div className="flex items-center">
                                <svg className="h-5 w-5 text-yellow-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div className="ml-3">
                                  <p className="text-sm text-yellow-800"><strong>{t('admin.cantCutAlreadySet')}</strong></p>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                            {teamPlayers.map((player) => {
                              if (!player || !player.id) return null;
                              if (!player.isEligibleForCantCut) return null;

                              const isDisabled = cantCutPlayer && cantCutPlayer.id !== player.id;

                              return (
                                <div
                                  key={`${player.id}-${player.seasonId || 'unknown'}`}
                                  className={`border rounded-lg p-4 transition-colors ${
                                    isDisabled
                                      ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                                      : cantCutPlayer?.id === player.id
                                        ? 'bg-green-50 border-green-200'
                                        : selectedPlayerId === player.id
                                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 cursor-pointer'
                                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-gray-400 cursor-pointer'
                                  }`}
                                  onClick={() => !isDisabled && setSelectedPlayerId(player.id)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                      <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                                        <span className="text-xl">🏀</span>
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-800 dark:text-gray-200">
                                          {player.firstName || 'Unknown'} {player.lastName || 'Player'}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                          {t('admin.overall')}: {player.overallRating || 'N/A'} | {t('admin.position')}: {player.position || 'N/A'}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      {cantCutPlayer?.id === player.id && (
                                        <span className="bg-green-100 dark:bg-green-900/30 text-green-800 px-2 py-1 rounded text-sm">{t('admin.currentCantCut')}</span>
                                      )}
                                      {selectedPlayerId === player.id && (
                                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 px-2 py-1 rounded text-sm">{t('admin.selected')}</span>
                                      )}
                                      <input type="radio" name="cantCutPlayer" value={player.id}
                                        checked={selectedPlayerId === player.id}
                                        onChange={() => !isDisabled && setSelectedPlayerId(player.id)}
                                        disabled={isDisabled}
                                        className={`h-4 w-4 text-blue-600 dark:text-blue-400 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`} />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div className="flex gap-3 mt-4">
                            <button onClick={handleCantCutSubmit}
                              disabled={saving || !selectedPlayerId || !!cantCutPlayer}
                              className="bg-blue-50 dark:bg-blue-900/200 hover:bg-blue-600 disabled:bg-gray-300 text-white px-6 py-2 rounded-lg font-medium">
                              {saving ? t('admin.saving') : t('admin.setCantCutPlayer')}
                            </button>
                            {selectedPlayerId && !cantCutPlayer && (
                              <button onClick={() => setSelectedPlayerId(null)} disabled={saving}
                                className="bg-gray-50 dark:bg-gray-8000 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg">
                                {t('admin.clearSelection')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {teamPlayers && teamPlayers.length === 0 && !teamLoading && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          <p>{t('admin.noEligiblePlayers')}</p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ======================== */}
        {/* League Settings Tab */}
        {/* ======================== */}
        {activeTab === 'league' && showLeagueTab && (
          <MatchReportWeights
            weights={weights}
            setWeights={setWeights}
            weightsSaving={weightsSaving}
            setWeightsSaving={setWeightsSaving}
            weightsMessage={weightsMessage}
            setWeightsMessage={setWeightsMessage}
          />
        )}
      </div>

      {/* Confirmation Dialog for Can't Cut */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">{t('admin.cantCutConfirmation')}</h3>
            <div className="mb-4">
              <p className="text-red-600 dark:text-red-400 font-medium mb-3">{t('admin.cantCutWarning')}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.permanentSelection')}</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-800" disabled={saving}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmCantCutSelection}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" disabled={saving}>
                {saving ? t('admin.saving') : t('admin.confirmSelection')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Sub-component: Match Report Performance Score Weights (admin only)
 */
const MatchReportWeights = ({ weights, setWeights, weightsSaving, setWeightsSaving, weightsMessage, setWeightsMessage }) => {
  const [localWeights, setLocalWeights] = useState(null);

  useEffect(() => {
    fetchWeights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (weights) {
      setLocalWeights({ ...weights });
    }
  }, [weights]);

  const fetchWeights = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/match-reports/weights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWeights(data);
        setLocalWeights(data);
      }
    } catch (e) {
      console.error('Error fetching weights:', e);
    }
  };

  const handleSave = async () => {
    setWeightsSaving(true);
    setWeightsMessage({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/match-reports/weights`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(localWeights)
      });
      const data = await res.json();
      if (res.ok) {
        setWeights(data.weights);
        setWeightsMessage({ type: 'success', text: 'Gewichtungen gespeichert!' });
        setTimeout(() => setWeightsMessage({ type: '', text: '' }), 3000);
      } else {
        setWeightsMessage({ type: 'error', text: data.error || 'Fehler beim Speichern' });
      }
    } catch (e) {
      setWeightsMessage({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setWeightsSaving(false);
    }
  };

  const handleReset = () => {
    setLocalWeights({
      points: 1.0,
      rebounds: 1.2,
      assists: 1.5,
      steals: 2.0,
      blocks: 2.0,
      turnovers: 1.5
    });
  };

  if (!localWeights) return null;

  const fields = [
    { key: 'points', label: 'Points (PTS)', icon: '🏀' },
    { key: 'rebounds', label: 'Rebounds (REB)', icon: '📊' },
    { key: 'assists', label: 'Assists (AST)', icon: '🎯' },
    { key: 'steals', label: 'Steals (STL)', icon: '🔥' },
    { key: 'blocks', label: 'Blocks (BLK)', icon: '🛡️' },
    { key: 'turnovers', label: 'Turnovers (TO)', icon: '⚠️', negative: true }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">📝 Spielbericht – Performance-Gewichtung</h2>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-800">
          Diese Gewichtungen bestimmen, wie die Top 3 Performer pro Team im automatischen Spielbericht berechnet werden.
          Formel: <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">Score = PTS×w + REB×w + AST×w + STL×w + BLK×w − TO×w</code>
        </p>
      </div>

      {weightsMessage.text && (
        <div className={`px-4 py-3 rounded mb-4 ${
          weightsMessage.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700 dark:text-green-400'
            : 'bg-red-50 border border-red-200 text-red-700 dark:text-red-400'
        }`}>
          {weightsMessage.text}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {fields.map(({ key, label, icon, negative }) => (
          <div key={key} className="border rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {icon} {label} {negative && <span className="text-red-500 text-xs">(negativ)</span>}
            </label>
            <input type="number" min="0" max="10" step="0.1"
              value={localWeights[key]}
              onChange={(e) => setLocalWeights({ ...localWeights, [key]: parseFloat(e.target.value) || 0 })}
              className="w-full border rounded-md px-3 py-2 text-center text-lg font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600" />
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button onClick={handleSave} disabled={weightsSaving}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-6 py-2 rounded-lg font-medium">
          {weightsSaving ? 'Speichern...' : 'Gewichtungen speichern'}
        </button>
        <button onClick={handleReset}
          className="bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg">
          Auf Standard zurücksetzen
        </button>
      </div>
    </div>
  );
};

export default Settings;
